import { FormProvider, getFormProps } from '@conform-to/react'
import { Box, Button, Flex, IconButton, Tooltip } from '@radix-ui/themes'
import { useLocation, useParams } from '@remix-run/react'
import * as React from 'react'
import { RxPlus } from 'react-icons/rx'
import { type Params } from 'react-router-dom'
import { useHydrated } from 'remix-utils/use-hydrated'
import { z } from 'zod'
import {
	FormDefaults,
	FormDefaultsHiddenField,
} from '#app/components/common/form-defaults.tsx'
import { SubmissionButton } from '#app/components/common/submission-button.tsx'
import { User_2 } from '#app/components/icon'
import { RatingInput } from '#app/components/ui/rating-input.tsx'
import { TextArea } from '#app/components/ui/text-area.tsx'
import { TextField } from '#app/components/ui/text-field.tsx'
import { type GetProduct } from '#app/models/ecommerce-providers/sylius/shop/fetch/product/get-product.ts'
import { type Locale } from '#app/models/locale.ts'
import { type App } from '#app/routes/_layout/component.route.tsx'
import {
	type FormEnum,
	ROOT_APP_VIEW,
} from '#app/routes/_layout/route-config.ts'
import { schemaDefault } from '#app/utils/form-defaults.ts'
import { useCreateProductReviewForm } from '#app/utils/hooks/forms/use-create-product-review-form.ts'

/* -------------------------------------------------------------------------------------------------
 * Create product review form
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	loggedIn = 'loggedIn',
	title = 'title',
	rating = 'rating',
	comment = 'comment',
	email = 'email',
	product = 'product',
}

const FORM: FormEnum = {
	id: 'create-product-review-form',
	generateAction: (params: Params) => `/products/${params.slug}/review/create`,
	fetcherKey: 'create-product-review',
	subaction: 'create-product-review',
	generateRedirect: (params: Params) => `/products/${params.slug}`,
} as const

const schema = z
	.object({
		...schemaDefault,
		...{
			[INPUTS.loggedIn]: z.string().optional(),
			[INPUTS.product]: z.string(),
			[INPUTS.title]: z
				.string({ required_error: 'validation.title.required' })
				.min(4, 'validation.title.min')
				.max(255, 'validation.title.max'),
			[INPUTS.rating]: z
				.number({ required_error: 'validation.rating.required' })
				.min(1, 'validation.rating.min')
				.max(5, 'validation.rating.max'),
			[INPUTS.comment]: z
				.string({ required_error: 'validation.comment.required' })
				.min(4, 'validation.comment.min')
				.max(2048, 'validation.comment.max'),
			[INPUTS.email]: z
				.string()
				.email('validation.email.email')
				.max(254, 'validation.email.max')
				.optional(),
		},
	})
	.refine(({ email, loggedIn }) => email || loggedIn, {
		path: [INPUTS.email],
		message: 'validation.email.required',
	})

type CreateProductReviewFormProps = {
	product: GetProduct
	locale: Locale
	loggedIn: boolean
	setApp: React.Dispatch<React.SetStateAction<App>>
}

const CreateProductReviewForm: React.FC<CreateProductReviewFormProps> = ({
	product,
	locale,
	loggedIn,
	setApp,
}) => {
	const {
		createProductReviewFetcher,
		createProductReviewFormValues,
		createProductReviewForm,
		createProductReviewFields,
		t,
	} = useCreateProductReviewForm({ product, loggedIn })

	const location = useLocation()
	const params = useParams()
	const isHydrated = useHydrated()

	return (
		<FormProvider context={createProductReviewForm.context}>
			<Flex asChild direction="column">
				<createProductReviewFetcher.Form
					method="post"
					action={`/${locale}${FORM.generateAction!(params)}${location.search}`}
					{...getFormProps(createProductReviewForm)}
					preventScrollReset
				>
					<FormDefaults
						form={createProductReviewForm}
						fields={createProductReviewFields}
						values={createProductReviewFormValues}
					/>
					<FormDefaultsHiddenField
						meta={createProductReviewFields[INPUTS.product]}
					/>
					{loggedIn && (
						<FormDefaultsHiddenField
							meta={createProductReviewFields[INPUTS.loggedIn]}
						/>
					)}
					<Flex direction="column" gapY="6">
						<TextField
							type="text"
							meta={createProductReviewFields[INPUTS.title]}
							t={t}
						>
							{t('label.title')}
						</TextField>
						<RatingInput
							label={t('label.rating')}
							meta={createProductReviewFields[INPUTS.rating]}
							t={t}
						/>
						<TextArea
							meta={createProductReviewFields[INPUTS.comment]}
							rows={5}
							t={t}
						>
							{t('label.comment')}
						</TextArea>
						{!loggedIn && (
							<Flex gapX="4">
								<TextField
									type="email"
									meta={createProductReviewFields[INPUTS.email]}
									t={t}
								>
									{t('label.email')}
								</TextField>
								<Box display={{ sm: 'none' }}>
									<Tooltip content={t('button.login')}>
										<IconButton
											type="button"
											variant="soft"
											radius="full"
											onClick={() =>
												setApp((prev) => ({
													...prev,
													modalView: ROOT_APP_VIEW.loginView,
													displayModal: true,
													options: {
														...prev.options,
														redirect: location.pathname,
														noJs: String(!isHydrated),
														preventScrollReset: true,
													},
												}))
											}
										>
											<User_2 className="w-5 fill-transparent stroke-[var(--accent-a11)] stroke-[18]" />
										</IconButton>
									</Tooltip>
								</Box>
								<Flex asChild display={{ initial: 'none', sm: 'flex' }}>
									<Button
										type="button"
										variant="soft"
										onClick={() =>
											setApp((prev) => ({
												...prev,
												modalView: ROOT_APP_VIEW.loginView,
												displayModal: true,
												options: {
													...prev.options,
													redirect: location.pathname,
													noJs: String(!isHydrated),
												},
											}))
										}
									>
										<User_2 className="w-5 fill-transparent stroke-[var(--accent-a11)] stroke-[18]" />
										{t('button.login')}
									</Button>
								</Flex>
							</Flex>
						)}
						<Button size="4" type="submit" mt="2">
							<SubmissionButton fetcher={createProductReviewFetcher}>
								<RxPlus size="1.2" />
								{t('button.createProductReviewSubmit')}
							</SubmissionButton>
						</Button>
					</Flex>
				</createProductReviewFetcher.Form>
			</Flex>
		</FormProvider>
	)
}
CreateProductReviewForm.displayName = 'CreateProductReviewForm'

/* -----------------------------------------------------------------------------------------------*/

export {
	type CreateProductReviewFormProps,
	CreateProductReviewForm,
	INPUTS as CREATE_PRODUCT_REVIEW_INPUTS,
	FORM as CREATE_PRODUCT_REVIEW_FORM,
	schema as createProductReviewSchema,
}
